import { Fragment } from 'react';
import { ModelRow, RegionEntry, regionCity, regionLabel } from '../types';
import {
  ResidencyScope, boundaryRegions, countryFromSelector, countryOf, countrySelector,
  geographyLabel, geographyOf, regionsByCountry, scopeOptionsFor, suggestRegion,
} from '../lib/regions';
import { lifecycleStatus, LocationEntry, groupByProvider, modelsAtLocation, rowKey, tierLabel } from '../lib/models';
import { CopyButton } from '../components/CopyButton';
import { CloudPill } from '../components/CloudPill';

interface Props {
  rows: ModelRow[];
  allRegions: string[];
  regionLabels: Record<string, string>;
  /** A literal region ID, or a country selector from countrySelector(). */
  region: string;
  onRegion: (r: string) => void;
  scope: ResidencyScope;
  onScope: (s: ResidencyScope) => void;
  onOpenModel: (modelId: string) => void;
}

function regionOptionLabel(r: string, regionLabels: Record<string, string>) {
  const city = regionLabels[r];
  const country = countryOf(r);
  const place = [city, country].filter(Boolean).join(', ');
  return place ? `${r} — ${place}` : r;
}

/** Label for either a literal region or a country selector. */
function selectorLabel(selector: string, regionLabels: Record<string, string>): string {
  return countryFromSelector(selector) ?? regionLabel(selector, regionLabels);
}

/** Region label with the deployment tier folded into the same parenthetical
 *  as the city, e.g. "uksouth (UK South · Provisioned)" — avoids stacking a
 *  second "(tier)" after regionLabel's own "(city)". */
function nativeRegionLabel(re: RegionEntry, regionLabels: Record<string, string>): string {
  const city = regionCity(re.region, regionLabels);
  const tier = tierLabel(re.inferenceTypesSupported);
  return `${re.region} (${tier ? `${city} · ${tier}` : city})`;
}

function EntryCard({ entry, regionLabels, onOpenModel }: {
  entry: LocationEntry;
  regionLabels: Record<string, string>;
  onOpenModel: (modelId: string) => void;
}) {
  const { row, native, nativeRegions, criOptions } = entry;
  // Scoped to the regions THIS card actually represents, not the whole
  // model — a model can be Active in one region and Legacy in another, so
  // the badge here must reflect what you'd actually get calling this card's
  // region(s), not some other region's status.
  const status = native ? lifecycleStatus(nativeRegions) : lifecycleStatus(row.regions);
  return (
    <div className="entry-card">
      <div className="entry-header">
        <span className="card-provider">{row.providerName}</span>
        <div className="card-badges">
          <CloudPill cloud={row.cloud} />
          {row.streamingSupported && <span className="badge badge-stream">stream</span>}
          {status === 'LEGACY' && <span className="badge badge-legacy">Legacy</span>}
          {status === 'MIXED' && (
            <span className="badge badge-mixed" title="Active in some regions shown here, Legacy/deprecated in others">
              Mixed status
            </span>
          )}
          {native
            ? <span className="badge badge-inregion">In-region</span>
            : <span className="badge badge-routed">Via routing</span>}
        </div>
      </div>

      <div className="entry-name">{row.modelName}</div>

      {native && (
        <>
          <div className="entry-id-row">
            <code className="model-id">{row.modelId}</code>
            <CopyButton text={row.modelId} />
          </div>
          <p className="entry-residency entry-residency-native">
            {nativeRegions.length === 1
              ? <>Data stays in {nativeRegionLabel(nativeRegions[0], regionLabels)}.</>
              : <>Native in: {nativeRegions.map(re => nativeRegionLabel(re, regionLabels)).join(' · ')}.</>}
          </p>
        </>
      )}

      {criOptions.map(({ profile, destinations }) => (
        <div key={profile.profileId} className="entry-cri">
          <div className="entry-id-row">
            <span className={`badge badge-zone-${profile.prefix}`}>{profile.prefix}</span>
            <code className="model-id">{profile.profileId}</code>
            <CopyButton text={profile.profileId} />
          </div>
          <p className="entry-residency">
            May be processed in:{' '}
            {destinations.map(d => regionLabel(d, regionLabels)).join(' · ')}
          </p>
        </div>
      ))}

      <button
        className="entry-global-link"
        aria-label={`Global availability for ${row.modelName}`}
        onClick={() => onOpenModel(rowKey(row))}
      >
        Global availability →
      </button>
    </div>
  );
}

export function LocationView({
  rows, allRegions, regionLabels, region, onRegion, scope, onScope, onOpenModel,
}: Props) {
  const byGeo = new Map<string, string[]>();
  for (const r of allRegions) {
    const geo = geographyOf(r) ?? 'other';
    if (!byGeo.has(geo)) byGeo.set(geo, []);
    byGeo.get(geo)!.push(r);
  }

  const selector = (
    <select
      id="region-select"
      className="region-select"
      value={region}
      onChange={e => onRegion(e.target.value)}
      aria-label="Workload region"
    >
      <option value="">Select a region…</option>
      {[...byGeo.entries()].map(([geo, regions]) => {
        // Within each geography, group by country so a country with 2+
        // regions (any cloud) gets one "all providers" entry ahead of its
        // individual regions — avoids picking eu-west-2 and uksouth separately.
        const byCountry = regionsByCountry(regions);
        const noCountry = regions.filter(r => !countryOf(r));
        return (
          <optgroup key={geo} label={geo === 'other' ? 'Other' : geographyLabel(geo)}>
            {[...byCountry.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([country, countryRegions]) => (
              <Fragment key={country}>
                {countryRegions.length > 1 && (
                  <option value={countrySelector(country)}>
                    {country} — all providers ({countryRegions.length})
                  </option>
                )}
                {countryRegions.map(r => (
                  <option key={r} value={r}>{regionOptionLabel(r, regionLabels)}</option>
                ))}
              </Fragment>
            ))}
            {noCountry.map(r => (
              <option key={r} value={r}>{regionOptionLabel(r, regionLabels)}</option>
            ))}
          </optgroup>
        );
      })}
    </select>
  );

  if (!region) {
    const suggestions = suggestRegion(allRegions);
    // Two suggestions sharing a country (e.g. eu-west-2 + uksouth, both UK)
    // merge into one "all providers" suggestion rather than two buttons.
    const suggestionCountries = new Set(
      suggestions.map(countryOf).filter((c): c is string => c != null),
    );
    const mergedCountry = suggestions.length >= 2 && suggestionCountries.size === 1
      ? [...suggestionCountries][0]
      : null;
    return (
      <div className="location-empty">
        <h2>Where is your workload?</h2>
        <p className="drawer-hint">
          Pick the region you call from — AWS or Azure — to see which models you
          can use and where your data could be processed.
        </p>
        {selector}
        {suggestions.length > 0 && (
          <div className="suggest-row">
            {mergedCountry ? (
              <button className="suggest-btn" onClick={() => onRegion(countrySelector(mergedCountry))}>
                Suggested: {mergedCountry} — all providers (based on your time zone)
              </button>
            ) : (
              suggestions.map(s => (
                <button key={s} className="suggest-btn" onClick={() => onRegion(s)}>
                  Suggested: {regionOptionLabel(s, regionLabels)} (based on your time zone)
                </button>
              ))
            )}
          </div>
        )}
      </div>
    );
  }

  const scopeOptions = scopeOptionsFor(region, allRegions);
  // A scope can become unavailable when the region changes; snap to strictest
  const effectiveScope = scopeOptions.some(o => o.scope === scope) ? scope : scopeOptions[0].scope;
  const tightestScope = scopeOptions[0].scope;
  const entries = modelsAtLocation(rows, region, effectiveScope, allRegions);
  const nativeEntries = entries.filter(e => e.native);
  const routedEntries = entries.filter(e => !e.native);
  const locationName = selectorLabel(region, regionLabels);

  // "Data may leave" is only true if some routing destination actually falls
  // outside the selected location's own boundary — for a country selector,
  // any within-country destination (e.g. a us.* CRI from us-east-1 to
  // us-east-2) never really leaves, even though it's a different region.
  const withinLocation = boundaryRegions(region, allRegions);
  const routedDestinations = routedEntries.flatMap(e => e.criOptions.flatMap(o => o.destinations));
  const routingStaysWithinLocation = routedDestinations.length > 0
    && routedDestinations.every(d => withinLocation.includes(d));

  return (
    <div>
      <div className="location-controls">
        <label className="location-label" htmlFor="region-select">Workload region</label>
        {selector}
        <div className="scope-control" role="radiogroup" aria-label="Data residency">
          <span className="location-label">Data residency:</span>
          {scopeOptions.map(o => (
            <button
              key={o.scope}
              role="radio"
              aria-checked={effectiveScope === o.scope}
              className={`scope-pill${effectiveScope === o.scope ? ' active' : ''}`}
              onClick={() => onScope(o.scope)}
            >
              {o.scope === tightestScope ? 'In Region' : `${o.label} — See Details`}
            </button>
          ))}
        </div>
      </div>

      <p className="location-summary">
        From <strong>{locationName}</strong>:{' '}
        {nativeEntries.length} models in-region
        {effectiveScope !== tightestScope && <>, +{routedEntries.length} more via cross-region routing</>}.
      </p>

      {entries.length === 0 && (
        <p className="empty-message">No models match the current region, scope, and filters.</p>
      )}

      {nativeEntries.length > 0 && (
        <section>
          <h3 className="location-section-title">In-region — data stays in {locationName}</h3>
          <div className="entry-clusters">
            {groupByProvider(nativeEntries).map(g => (
              <div key={g.provider} className="entry-cluster">
                <div className="entry-cluster-head">{g.provider}</div>
                <div className="entry-grid">
                  {g.items.map(e => (
                    <EntryCard key={rowKey(e.row)} entry={e} regionLabels={regionLabels} onOpenModel={onOpenModel} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {routedEntries.length > 0 && (
        <section>
          <h3 className="location-section-title">
            {routingStaysWithinLocation
              ? <>Via cross-region routing — stays within {locationName}, but may move to a different location there (destinations shown per model)</>
              : <>Via cross-region routing — data may leave {locationName} (destinations shown per model)</>}
          </h3>
          <div className="entry-clusters">
            {groupByProvider(routedEntries).map(g => (
              <div key={g.provider} className="entry-cluster">
                <div className="entry-cluster-head">{g.provider}</div>
                <div className="entry-grid">
                  {g.items.map(e => (
                    <EntryCard key={rowKey(e.row)} entry={e} regionLabels={regionLabels} onOpenModel={onOpenModel} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
